import { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence } from 'framer-motion'
import {
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore'
import { firestoreDb } from '../services/firebase'
import { getPublicProfiles } from '../services/publicProfileService'
import { useDashboard } from '../context/useDashboard'
import { WORKSPACE_DEFAULT_CHECKLIST_ID, type TimelineElement } from '../context/TimelineContext'
import { useTimeline } from '../context/useTimeline'
import { useRecurringLens } from '../context/recurringLensContextCore'
import { useDisplayName } from '../hooks/useDisplayName'
import { useNavigation } from '../context/useNavigation'
import type { ChecklistItem, ChecklistMemberOption } from '../types/schema'
import { roleCanPerform } from '../utils/workspacePermissions'
import { getWorkspaceOwnerIds } from '../utils/workspaceOwners'
import { usePessimisticLock } from '../hooks/usePessimisticLock'
import { useWorkspace } from '../context/useWorkspace'
import { useAuth } from '../context/useAuth'
import {
  normalizeChecklistInputLimits,
} from '../utils/checklistLimits'
import type { ChecklistBaseOperationalPermissions } from '../utils/checklistOperationalPermissions'
import { isNoSignInPreviewActive } from '../debug'
import { isPersonalWorkspace } from '../utils/workspaceKind'

import {
  normalizeChecklistMemberRole,
  resolveChecklistMemberDisplayName,
} from './ChecklistDrawingBoard/memberOptions'
import {
  normalizeChecklistPrerequisiteIds,
  normalizeBoardItem,
  type ChecklistCreateRequest,
  type BoardItemInput,
} from './ChecklistDrawingBoard/boardItem'
import {
  invalidateChecklistBoardSessionSnapshot,
} from './ChecklistDrawingBoard/sessionSnapshot'
import {
  getNodeTopAttachmentY,
  getNodeBottomAttachmentY,
  getAnchorPoint,
  getNodeWireEndpoint,
  assertAndSemanticsForPrereqs,
  isGateConnected,
  type WireEndpointSlot,
} from './ChecklistDrawingBoard/wireGeometry'
import {
  executeDeleteGate,
} from './ChecklistDrawingBoard/gateActions'
import {
  type DragState,
} from './ChecklistDrawingBoard/canvasMisc'
import {
  getConnectedChecklistNodeIds,
  reconcileLiveChecklistBoardNodes,
  type LiveChecklistReconciliationResult,
} from './ChecklistDrawingBoard/liveReconciliation'
import {
  makeCanvasEscapeHandler,
} from './ChecklistDrawingBoard/escapeHandler'
import { useBoardCoreState } from './ChecklistDrawingBoard/useBoardCoreState'
import { useBoardViewport } from './ChecklistDrawingBoard/useBoardViewport'
import { useLiveReconciliation } from './ChecklistDrawingBoard/useLiveReconciliation'
import { useBoardLoad } from './ChecklistDrawingBoard/useBoardLoad'
import { useRecurringPageSubscription } from './ChecklistDrawingBoard/useRecurringPageSubscription'
import { useBoardSession } from './ChecklistDrawingBoard/useBoardSession'
import { useBoardNodesAndMirrors } from './ChecklistDrawingBoard/useBoardNodesAndMirrors'
import { useVisionboardAndMiniMap } from './ChecklistDrawingBoard/useVisionboardAndMiniMap'
import { useBoardPlacement } from './ChecklistDrawingBoard/useBoardPlacement'
import { useDraftChecklist } from './ChecklistDrawingBoard/useDraftChecklist'
import { useAutoSpawnTemplates } from './ChecklistDrawingBoard/useAutoSpawnTemplates'
import { useBoardMarkdownSync } from './ChecklistDrawingBoard/useBoardMarkdownSync'
import { useNodeStatus } from './ChecklistDrawingBoard/useNodeStatus'
import { useBoardWireGeometry } from './ChecklistDrawingBoard/useBoardWireGeometry'
import { useBoardPointerHandlers } from './ChecklistDrawingBoard/useBoardPointerHandlers'
import { BoardSessionTools } from './ChecklistDrawingBoard/BoardSessionTools'
import { BoardHeader } from './ChecklistDrawingBoard/BoardHeader'
import { BoardViewport } from './ChecklistDrawingBoard/BoardViewport'
import { BoardOverlays } from './ChecklistDrawingBoard/BoardOverlays'
import { BoardDrawers } from './ChecklistDrawingBoard/BoardDrawers'
import { BoardDialogs } from './ChecklistDrawingBoard/BoardDialogs'

export {
  resolveChecklistMemberDisplayName,
  normalizeChecklistPrerequisiteIds,
  normalizeBoardItem,
  invalidateChecklistBoardSessionSnapshot,
  getNodeTopAttachmentY,
  getNodeBottomAttachmentY,
  getAnchorPoint,
  getNodeWireEndpoint,
  assertAndSemanticsForPrereqs,
  isGateConnected,
  executeDeleteGate,
  getConnectedChecklistNodeIds,
  reconcileLiveChecklistBoardNodes,
  makeCanvasEscapeHandler,
  type BoardItemInput,
  type WireEndpointSlot,
  type LiveChecklistReconciliationResult,
}

export const ChecklistDrawingBoard = ({
  item,
  onRequestClose,
  isCompact = false,
  onCreateChecklist,
  onImportChecklist,
  alwaysVisible = false,
}: {
  item: TimelineElement
  onRequestClose?: () => void
  onOpenExplore?: () => void
  isCompact?: boolean
  onCreateChecklist?: (request?: ChecklistCreateRequest) => void
  onImportChecklist?: () => void
  /** When true, bypass the `isCanvasMaximized` gate so the board renders
   *  inline (used by the Daily / Weekly recurring canvas shell). */
  alwaysVisible?: boolean
}) => {
  const {
    activeCanvasNodeId,
    closeCanvasWorkspace,
    elements,
    isCinematicModeActive,
    isCanvasMaximized,
    setIsCinematicModeActive,
  } = useTimeline()
  const recurringLens = useRecurringLens()

  // Ancestry chain of the checklist's underlying element (workspace default
  // checklists have no parent, so the chain is empty in that case). Each
  // entry is rendered as a clickable breadcrumb pill — clicking jumps the
  // user out of the canvas to that level via `useNavigation` below.
  const breadcrumbChain = useMemo<TimelineElement[]>(() => {
    // Under a recurring lens the mounted `item` is a synthetic recurring
    // shell (id = `recurring:{ws}:{mode}`), so we cannot start the walk
    // from it — it has no real Goal/Task/Subtask ancestry. Instead, start
    // from the dashboard's `activeCanvasNodeId` (the sidebar's current
    // navigation level) so clicking Goal → Task → Subtask in the sidebar
    // still surfaces a working breadcrumb switcher on the Daily/Weekly
    // canvases. If nothing is focused there, fall through to an empty
    // chain (workspace root).
    const lookup = new Map(elements.map((element) => [element.id, element]))
    const seed: TimelineElement | undefined = recurringLens
      ? (activeCanvasNodeId ? lookup.get(activeCanvasNodeId) : undefined)
      : (activeCanvasNodeId ? lookup.get(activeCanvasNodeId) ?? item : item)
    if (!seed) return []
    const chain: TimelineElement[] = []
    let current: TimelineElement | undefined = seed

    while (current) {
      // Skip the synthetic workspace-default-checklist stand-in — it has no
      // real navigation target and was leaking into the dashboard breadcrumb.
      if (current.id !== WORKSPACE_DEFAULT_CHECKLIST_ID) {
        chain.unshift(current)
      }
      current = current.parentId ? lookup.get(current.parentId) : undefined
    }

    return chain
  }, [activeCanvasNodeId, elements, item, recurringLens])

  useEffect(() => {
    if (!isCanvasMaximized) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const handleEscape = makeCanvasEscapeHandler({ isCinematicModeActive, setIsCinematicModeActive })
    window.addEventListener('keydown', handleEscape)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleEscape)
    }
  }, [isCanvasMaximized, isCinematicModeActive, setIsCinematicModeActive])

  const isVisible = alwaysVisible || isCanvasMaximized
  const handleClose = onRequestClose ?? closeCanvasWorkspace
  const surfaceIdentityKey = recurringLens
    ? `${item.id}:${recurringLens.focusedKey}:${breadcrumbChain[breadcrumbChain.length - 1]?.id ?? WORKSPACE_DEFAULT_CHECKLIST_ID}`
    : item.id

  return (
    <AnimatePresence>
      {isVisible && (
        <ChecklistDrawingBoardSurface
          key={surfaceIdentityKey}
          item={item}
          breadcrumbChain={breadcrumbChain}
          onClose={handleClose}
          isCompact={isCompact}
          alwaysVisible={alwaysVisible}
          onCreateChecklist={onCreateChecklist}
          onImportChecklist={onImportChecklist}
        />
      )}
    </AnimatePresence>
  )
}

const ChecklistDrawingBoardSurface = ({
  item,
  breadcrumbChain,
  onClose,
  isCompact,
  alwaysVisible,
  onCreateChecklist: _onCreateChecklist,
  onImportChecklist,
}: {
  item: TimelineElement
  breadcrumbChain: TimelineElement[]
  onClose: () => void
  isCompact: boolean
  alwaysVisible: boolean
  onCreateChecklist?: (request?: ChecklistCreateRequest) => void
  onImportChecklist?: () => void
}) => {
  const {
    elements,
    isPublicReadOnly,
    viewportState,
    setCanvasTransform,
    panCanvasToCoordinates,
    isCinematicModeActive,
    setIsCinematicModeActive,
    isCalendarShelfOpen,
    setIsCalendarShelfOpen,
    markdownMetadata,
    setMarkdownMetadata,
    isGestureKnifeActive,
    setIsGestureKnifeActive,
    applyStripboardDelta,
    checklistItems: timelineChecklistItems,
    isLoading: isTimelineLoading,
    canPerformOnNode,
  } = useTimeline()
  const { activeWorkspaceId, activeRole, workspaces, canPerform } = useWorkspace()
  const { selectedChecklistNodeId: selectedNodeId, drawerState, setSelectedChecklistNodeId: setSelectedNodeId, closeChecklistNode } = useDashboard()
  const { currentUser, userProfile } = useAuth()
  const currentDisplayName = useDisplayName(userProfile ?? currentUser)
  const { goToDashboard, goToGoal, goToTask, goToSubTask } = useNavigation()
  const { maximizeCanvasWorkspace } = useTimeline()
  // When the board is mounted inside a RecurringLensProvider (Daily / Weekly
  // canvas), completion writes route through the singleton service's
  // setOccurrenceCompletion using the focused occurrence key instead of
  // hitting the master `isCompleted` field directly.
  const recurringLens = useRecurringLens()
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId || workspace.id === item.workspaceId)
  const isDebugCanvas = isNoSignInPreviewActive()
  const [workspaceMembers, setWorkspaceMembers] = useState<ChecklistMemberOption[]>([])

  useEffect(() => {
    if (isDebugCanvas) {
      setWorkspaceMembers(currentUser ? [{
        userId: currentUser.uid,
        displayName: currentDisplayName || currentUser.email || 'Debug user',
        email: currentUser.email ?? '',
        role: roleCanPerform(activeRole, 'EDITOR') ? activeRole : 'VIEWER',
      }, {
        userId: 'debug-admin-user',
        displayName: 'Debug Admin',
        email: 'debug-admin@example.test',
        role: 'ADMIN',
      }, {
        userId: 'debug-editor-user',
        displayName: 'Debug Editor',
        email: 'debug-editor@example.test',
        role: 'EDITOR',
      }, {
        userId: 'debug-helper-user',
        displayName: 'Debug Helper',
        email: 'debug-helper@example.test',
        role: 'HELPER',
      }] : [])
      return
    }
    let cancelled = false
    const loadMembers = async () => {
      const membershipSnapshot = await getDocs(query(
        collection(firestoreDb, 'workspace_members'),
        where('workspaceId', '==', item.workspaceId),
      ))
      const memberRows = membershipSnapshot.docs
        .map((membershipDoc) => membershipDoc.data() as Record<string, unknown>)
        .filter((data) => !data.status || data.status === 'ACTIVE')

      const memberUids = memberRows
        .map((data) => typeof data.uid === 'string' ? data.uid : typeof data.userId === 'string' ? data.userId : '')
        .filter(Boolean)

      const allUids = [...memberUids]
      const ownerIds = getWorkspaceOwnerIds(activeWorkspace)
      for (const ownerId of ownerIds) {
        if (!allUids.includes(ownerId)) {
          allUids.push(ownerId)
        }
      }

      const publicProfiles = await getPublicProfiles(allUids)

      const identities = memberRows.map((data) => {
        const userId = typeof data.uid === 'string' ? data.uid : typeof data.userId === 'string' ? data.userId : ''
        if (!userId) return null
        const profile = publicProfiles.get(userId)
        const email = typeof data.email === 'string'
          ? data.email
          : typeof data.user_email === 'string'
            ? data.user_email
            : ''
        const displayName = resolveChecklistMemberDisplayName(data, profile, email)
        const role = normalizeChecklistMemberRole(data.role)
        return { userId, displayName, email, role } satisfies ChecklistMemberOption
      })

      for (const ownerId of ownerIds) {
        if (!identities.some((member) => member?.userId === ownerId)) {
          const ownerProfile = publicProfiles.get(ownerId)
          identities.push({
            userId: ownerId,
            displayName: ownerProfile?.displayName || 'Workspace admin',
            email: '',
            role: 'OWNER',
          })
        }
      }
      if (!cancelled) {
        const validMembers: ChecklistMemberOption[] = []
        for (const member of identities) {
          if (member) validMembers.push(member)
        }
        setWorkspaceMembers(validMembers)
      }
    }
    void loadMembers().catch((error) => console.error('[ChecklistDrawingBoard] member load failed', error))
    return () => { cancelled = true }
  }, [activeRole, activeWorkspace?.ownerId, currentUser, isDebugCanvas, item.workspaceId])

  // Breadcrumb click handlers. The canvas stays maximized — instead of
  // closing it and unmounting the surface, we re-point the canvas at the
  // clicked item's checklist via `maximizeCanvasWorkspace(id)` AND navigate
  // the dashboard underneath to the matching level. The two surfaces always
  // move together so the canvas-on-top and the dashboard-below can never
  // sit at different breadcrumb levels.
  //
  // Walking the chain (built top-down by the parent: Goal → Task → Subtask)
  // gives us the goal/task IDs needed to call goToTask / goToSubTask.
  const chainGoal = breadcrumbChain.find((element) => element.type === 'GOAL') ?? null
  const chainTask = breadcrumbChain.find((element) => element.type === 'TASK') ?? null
  const jumpToDashboardFromBoard = () => {
    requestBoardNavigation(() => {
      // Dashboard level has no per-element checklist of its own, so the canvas
      // falls back to the workspace default checklist — same item the dashboard
      // header's "Open Checklists" button targets at dashboard level.
      maximizeCanvasWorkspace(WORKSPACE_DEFAULT_CHECKLIST_ID)
      goToDashboard()
    })
  }
  const jumpToChainItem = (element: TimelineElement) => {
    requestBoardNavigation(() => {
      maximizeCanvasWorkspace(element.id)
      if (element.type === 'GOAL') {
        goToGoal(element.id)
        return
      }
      if (element.type === 'TASK' && chainGoal) {
        goToTask(chainGoal.id, element.id)
        return
      }
      if (element.type === 'SUBTASK' && chainGoal && chainTask) {
        goToSubTask(chainGoal.id, chainTask.id, element.id)
      }
    })
  }
  const checklistLimits = normalizeChecklistInputLimits(activeWorkspace?.checklistInputLimits, isPersonalWorkspace(activeWorkspace))
  const canEditStructure = !isPublicReadOnly && canPerform('checklist.edit_structure')
  const canEditCanvas = !isPublicReadOnly && canPerform('checklist.edit_canvas')
  const canCreateChecklist = !isPublicReadOnly && canPerform('checklist.create')
  const canDeleteChecklist = !isPublicReadOnly && canPerform('checklist.delete')
  const canInteractWithChecklist = !isPublicReadOnly
  const baseOperationalPermissions: ChecklistBaseOperationalPermissions = {
    assign: canPerform('checklist.assign'),
    status: canPerform('checklist.change_status'),
    proof: canPerform('checklist.manage_proof'),
    memo: canPerform('checklist.manage_memo'),
    comment: canPerform('checklist.comment'),
    locationCheckin: canPerform('checklist.location_checkin'),
  }
  const { getExternalLock } = usePessimisticLock()
  const boardRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<DragState>(null)
  const {
    panVelocityRef,
    panMomentumRafRef,
    stopPanMomentum,
    scrollFrameRef,
    tooltipTouchTimerRef,
    lastChecklistMirrorRef,
    timelineChecklistItemsRef,
    hasBoardSnapshotRef,
    boardDocumentIdsRef,
    deletedSourceIdsRef,
    weightCommitTimersRef,
    cursorWriteTimerRef,
    latestCursorPayloadRef,
    breakingWireCounterRef,
    nodes,
    setNodes,
    nodesRef,
    resourceReadiness,
    optimisticPositions,
    setOptimisticPositions,
    optimisticPositionsRef,
    optimisticWeights,
    baselineNodesRef,
    undoStackRef,
    pendingDeletionsRef,
    pendingCloseActionRef,
    pendingOpenChecklistItemIdRef,
    isBoardDirty,
    setIsBoardDirty,
    isBoardDirtyRef,
    canUndoBoard,
    setCanUndoBoard,
    closeBoardDialogOpen,
    setCloseBoardDialogOpen,
    dialogBusyAction,
    setDialogBusyAction,
    closeDialogError,
    setCloseDialogError,
    gateDialogError,
    setGateDialogError,
    boardSessionSnapshotKey,
    openGatesDialog,
    setOpenGatesDialog,
    draftDrawerNode,
    setDraftDrawerNode,
  } = useBoardCoreState({
    timelineChecklistItems,
    activeWorkspace,
    elements,
    recurringLens,
    breadcrumbChain,
    item,
  })
  const {
    boardScale,
    BOARD_SCALE_MIN,
    BOARD_SCALE_MAX,
    adjustBoardScale,
    isPlacingNode,
    setIsPlacingNode,
    wireDraft,
    setWireDraft,
    flashMessage,
    setFlashMessage,
    breakingWires,
    setBreakingWires,
    viewportSize,
    setViewportSize,
    checklistHover,
    collectChecklistAvoidZones,
    showChecklistNodeHover,
    showChecklistWireHover,
    hideChecklistHover,
    externalWorkspaceProgressByToken,
    setExternalWorkspaceProgressByToken,
    isBoardToolMenuOpen,
    setIsBoardToolMenuOpen,
    isMiniMapVisible,
    setIsMiniMapVisible,
    isMarkdownPanelOpen,
    isClipboardWatcherEnabled,
    setTimerTick,
  } = useBoardViewport({
    timelineChecklistItemsRef,
    timelineChecklistItems,
    isBoardDirty,
    hasBoardSnapshotRef,
    nodes,
    boardSessionSnapshotKey,
    boardRef,
    isCompact,
  })
  void BOARD_SCALE_MIN
  void BOARD_SCALE_MAX
  const {
    hasElementMirror,
    dashboardElements,
    dashboardChecklistRootIds,
    isDashboardChecklistContainer,
    arrangeWorkspaceNodes,
  } = useLiveReconciliation({
    item,
    recurringLens,
    elements,
    isBoardDirty,
    hasBoardSnapshotRef,
    timelineChecklistItems,
    nodesRef,
    deletedSourceIdsRef,
    activeWorkspace,
    currentUser,
    isBoardDirtyRef,
    setNodes,
    optimisticPositionsRef,
    boardDocumentIdsRef,
    baselineNodesRef,
    isCinematicModeActive,
    setIsCinematicModeActive,
    canEditCanvas,
    setIsGestureKnifeActive,
    weightCommitTimersRef,
    scrollFrameRef,
    tooltipTouchTimerRef,
    cursorWriteTimerRef,
    boardRef,
    setViewportSize,
  })

  const { setFocusedPageRefreshToken } = useBoardLoad({
    alwaysVisible,
    setTimerTick,
    item,
    recurringLens,
    setNodes,
    baselineNodesRef,
    undoStackRef,
    pendingDeletionsRef,
    setIsBoardDirty,
    setCanUndoBoard,
    setOptimisticPositions,
    isDashboardChecklistContainer,
    hasBoardSnapshotRef,
    activeWorkspace,
    currentUser,
    arrangeWorkspaceNodes,
    boardDocumentIdsRef,
    boardSessionSnapshotKey,
    isDebugCanvas,
    timelineChecklistItemsRef,
    isTimelineLoading,
    canEditCanvas,
    dashboardElements,
    deletedSourceIdsRef,
    boardRef,
    setViewportSize,
    setCanvasTransform,
  })

  useRecurringPageSubscription({
    recurringLens,
    item,
    setFocusedPageRefreshToken,
  })

  const {
    commitLocalNodes,
    restoreBoard,
    undoBoard,
    flashLocal,
    syncChecklistBookLayoutForType,
    buildOpenGateCleanupSnapshot,
    persistBoardSnapshot,
    saveBoard,
    completeBoardClose,
    cancelBoardClose,
    requestBoardNavigation,
    localReciprocalLinks,
    nodesById,
    visibleContainerIds,
    activeCanvasContainerId,
  } = useBoardSession({
    undoStackRef,
    nodes,
    setCanUndoBoard,
    setNodes,
    setIsBoardDirty,
    baselineNodesRef,
    setOptimisticPositions,
    pendingDeletionsRef,
    setFlashMessage,
    boardSessionSnapshotKey,
    currentUser,
    currentDisplayName,
    item,
    recurringLens,
    boardDocumentIdsRef,
    canEditCanvas,
    canDeleteChecklist,
    canCreateChecklist,
    isDebugCanvas,
    optimisticPositions,
    optimisticWeights,
    dialogBusyAction,
    setGateDialogError,
    setOpenGatesDialog,
    pendingCloseActionRef,
    setDialogBusyAction,
    setCloseDialogError,
    onClose,
    setCloseBoardDialogOpen,
    isBoardDirty,
    breadcrumbChain,
    dashboardChecklistRootIds,
    dashboardElements,
  })

  const {
    recurringParentEligibility,
    canAddChecklistHere,
    shouldMirrorNodeToElement,
    boardScopedNodes,
    displayNodes,
    spreadCanvasNodes,
    elementById,
    nodeParentPresentation,
  } = useBoardNodesAndMirrors({
    elements,
    activeCanvasContainerId,
    recurringLens,
    canCreateChecklist,
    nodes,
    localReciprocalLinks,
    optimisticWeights,
    optimisticPositions,
    visibleContainerIds,
    canEditCanvas,
    flashLocal,
    commitLocalNodes,
    setOptimisticPositions,
    isDebugCanvas,
    setExternalWorkspaceProgressByToken,
    canEditStructure,
    hasElementMirror,
    item,
    lastChecklistMirrorRef,
    hasBoardSnapshotRef,
  })

  const {
    visionboardChecklistItems,
    setVisionboardChecklistItems,
    boardMetrics,
    miniMapItems,
    syncCanvasTransformFromScroll,
    handleMiniMapPan,
    lockedNodeIds,
    clientToWorld,
    captureCursorPresence,
    showFlash,
    refreshMarkdownSyncMetadata,
  } = useVisionboardAndMiniMap({
    elements,
    viewportSize,
    displayNodes,
    boardRef,
    setViewportSize,
    scrollFrameRef,
    setCanvasTransform,
    panCanvasToCoordinates,
    nodesById,
    setSelectedNodeId,
    boardScale,
    pendingOpenChecklistItemIdRef,
    elementById,
    externalWorkspaceProgressByToken,
    currentUser,
    latestCursorPayloadRef,
    item,
    currentDisplayName,
    setFlashMessage,
    markdownMetadata,
    setMarkdownMetadata,
    recurringLens,
  })

  const {
    collectPlacementBoxes,
    getNextFreePosition,
    getNextChecklistGridPosition,
    resolveDropPosition,
    getRenderedNode,
    getRenderedAnchorPoint,
    getRenderedWireEndpoint,
    animateBreakingWire,
  } = useBoardPlacement({
    boardScopedNodes,
    optimisticPositions,
    boardMetrics,
    breakingWireCounterRef,
    setBreakingWires,
  })

  const {
    startNewDraftChecklist,
    persistDraftChecklist,
    addGateAt,
    handleCalendarDrop,
    handleClipboardBlueprintExtract,
  } = useDraftChecklist({
    checklistLimits,
    recurringLens,
    activeCanvasContainerId,
    item,
    currentUser,
    activeWorkspace,
    currentDisplayName,
    getNextChecklistGridPosition,
    canEditStructure,
    canCreateChecklist,
    showFlash,
    setDraftDrawerNode,
    nodes,
    elements,
    isDebugCanvas,
    commitLocalNodes,
    boardDocumentIdsRef,
    setSelectedNodeId,
    hasElementMirror,
    lastChecklistMirrorRef,
    syncChecklistBookLayoutForType,
    setNodes,
    canEditCanvas,
    collectPlacementBoxes,
    boardRef,
    viewportState,
    boardMetrics,
    clientToWorld,
    setVisionboardChecklistItems,
    activeWorkspaceId,
    getNextFreePosition,
  })
  // handleWeightDialWheel removed — the bubble no longer has a weight knob.
  // Weight is edited via the drawer's ScrollDial (deferred to drawer Save).

  const moveNodeToVisionboardRef = useRef<(node: ChecklistItem) => Promise<void>>(() => Promise.resolve())

  const {
    handleBoardPointerDown,
    handleNodePointerDown,
    handleAnchorPointerDown,
    handleBoardNodeMouseEnter,
    handleBoardNodeMouseMove,
    handleBoardNodeTouchStart,
    clearBoardNodeTooltip,
    deleteGateRequest,
    deleteOpenGate,
    handlePointerMove,
    handlePointerUp,
  } = useBoardPointerHandlers({
    isPlacingNode,
    setIsPlacingNode,
    clientToWorld,
    startNewDraftChecklist,
    stopPanMomentum,
    panVelocityRef,
    dragRef,
    canEditCanvas,
    boardRef,
    getRenderedAnchorPoint,
    setWireDraft,
    tooltipTouchTimerRef,
    showChecklistNodeHover,
    hideChecklistHover,
    canDeleteChecklist,
    displayNodes,
    boardDocumentIdsRef,
    flashLocal,
    showFlash,
    pendingDeletionsRef,
    commitLocalNodes,
    captureCursorPresence,
    setOptimisticPositions,
    setViewportSize,
    nodesById,
    resolveDropPosition,
    boardScopedNodes,
    isCalendarShelfOpen,
    moveNodeToVisionboard: (node) => moveNodeToVisionboardRef.current(node),
    setSelectedNodeId,
    animateBreakingWire,
    boardScale,
    panMomentumRafRef,
  })

  const { spawnAutoTemplateNodes } = useAutoSpawnTemplates({
    displayNodes,
    item,
    activeWorkspace,
    collectPlacementBoxes,
    currentUser,
    currentDisplayName,
    showFlash,
  })

  const {
    getNodeOperationalAccess,
    updateTitle,
    updateWeightage,
    updateNodeVerification,
    updateNodeAssignment,
    getConnectorRendering,
    updateNodeStatus,
    deleteNode,
    moveNodeToVisionboard,
  } = useNodeStatus({
    canPerformOnNode,
    currentUser,
    activeRole,
    canInteractWithChecklist,
    checklistLimits,
    canEditStructure,
    setNodes,
    currentDisplayName,
    refreshMarkdownSyncMetadata,
    hasElementMirror,
    recurringLens,
    showFlash,
    applyStripboardDelta,
    item,
    spawnAutoTemplateNodes,
    isDebugCanvas,
    canDeleteChecklist,
    deletedSourceIdsRef,
    nodes,
    shouldMirrorNodeToElement,
    lastChecklistMirrorRef,
    setSelectedNodeId,
    setVisionboardChecklistItems,
    elementById,
  })
  moveNodeToVisionboardRef.current = moveNodeToVisionboard

  const { linkMarkdownRepository, applyMarkdownInboundSync } = useBoardMarkdownSync({
    canEditStructure,
    checklistLimits,
    currentDisplayName,
    currentUser,
    hasElementMirror,
    item,
    nodes,
    setMarkdownMetadata,
    setNodes,
    showFlash,
  })

  const {
    connectorPaths,
    sliceableWires,
    sliceDependencyWires,
    wireDraftPath,
  } = useBoardWireGeometry({
    displayNodes,
    boardScopedNodes,
    getConnectorRendering,
    nodesById,
    canEditCanvas,
    animateBreakingWire,
    commitLocalNodes,
    showFlash,
    wireDraft,
    boardMetrics,
    optimisticPositions,
    getRenderedNode,
    getRenderedAnchorPoint,
    getRenderedWireEndpoint,
    boardScale,
    viewportSize,
    nodes,
  })

  const selectedNode = selectedNodeId ? nodesById.get(selectedNodeId) ?? null : null
  const selectedNodeOperationalAccess = selectedNode ? getNodeOperationalAccess(selectedNode) : null


  return (
    <div
      className={`checklistBoardShell checklistBoardShell--page checklistBoardShell--flat overflow-hidden ${isCinematicModeActive ? 'isCinematicMode' : ''} ${isCompact ? 'isCompact' : ''}`}
    >
      {/*
       * Top control bar (Return button + long ancestry string crumb) removed.
       * Navigation out of the canvas is now driven entirely by the clickable
       * breadcrumb rendered at the top of the panel header below — matching
       * the dashboard's own breadcrumb in position and styling so the canvas
       * and dashboard read as the same surface.
       */}
      <section className={`checklistBoardPanel themeSurface checklistBoardPanelFullscreen ${isCinematicModeActive ? 'isCinematicMode' : ''} ${isCalendarShelfOpen ? 'isVisionboardShelfOpen' : ''}`}>
      <BoardHeader
        item={item}
        dashboardElements={dashboardElements}
        breadcrumbChain={breadcrumbChain}
        jumpToDashboardFromBoard={jumpToDashboardFromBoard}
        jumpToChainItem={jumpToChainItem}
      >
        <BoardSessionTools
          canEditCanvas={canEditCanvas}
          canCreateChecklist={canCreateChecklist}
          addGateAt={addGateAt}
          getNextFreePosition={getNextFreePosition}
          isGestureKnifeActive={isGestureKnifeActive}
          setIsGestureKnifeActive={setIsGestureKnifeActive}
          boardScale={boardScale}
          adjustBoardScale={adjustBoardScale}
          isBoardDirty={isBoardDirty}
          saveBoard={saveBoard}
          canUndoBoard={canUndoBoard}
          undoBoard={undoBoard}
          restoreBoard={restoreBoard}
          boardScopedNodes={boardScopedNodes}
          spreadCanvasNodes={spreadCanvasNodes}
          isBoardToolMenuOpen={isBoardToolMenuOpen}
          setIsBoardToolMenuOpen={setIsBoardToolMenuOpen}
          canAddChecklistHere={canAddChecklistHere}
          startNewDraftChecklist={startNewDraftChecklist}
          recurringLens={recurringLens}
          onImportChecklist={onImportChecklist}
          recurringParentEligibility={recurringParentEligibility}
          isMiniMapVisible={isMiniMapVisible}
          setIsMiniMapVisible={setIsMiniMapVisible}
          canEditStructure={canEditStructure}
          setIsCalendarShelfOpen={setIsCalendarShelfOpen}
        />
      </BoardHeader>

      <BoardViewport
        boardRef={boardRef}
        isPlacingNode={isPlacingNode}
        viewportSize={viewportSize}
        handleBoardPointerDown={handleBoardPointerDown}
        handlePointerMove={handlePointerMove}
        handlePointerUp={handlePointerUp}
        syncCanvasTransformFromScroll={syncCanvasTransformFromScroll}
        canEditStructure={canEditStructure}
        handleCalendarDrop={handleCalendarDrop}
        boardMetrics={boardMetrics}
        boardScale={boardScale}
        flashMessage={flashMessage}
        nodes={nodes}
        connectorPaths={connectorPaths}
        nodesById={nodesById}
        showChecklistWireHover={showChecklistWireHover}
        hideChecklistHover={hideChecklistHover}
        breakingWires={breakingWires}
        wireDraftPath={wireDraftPath}
        displayNodes={displayNodes}
        lockedNodeIds={lockedNodeIds}
        getExternalLock={getExternalLock}
        elementById={elementById}
        getRenderedNode={getRenderedNode}
        canEditCanvas={canEditCanvas}
        boardDocumentIdsRef={boardDocumentIdsRef}
        canDeleteChecklist={canDeleteChecklist}
        handleNodePointerDown={handleNodePointerDown}
        handleAnchorPointerDown={handleAnchorPointerDown}
        deleteGateRequest={deleteGateRequest}
        deleteOpenGate={deleteOpenGate}
        getNodeOperationalAccess={getNodeOperationalAccess}
        workspaceMembers={workspaceMembers}
        nodeParentPresentation={nodeParentPresentation}
        handleBoardNodeMouseEnter={handleBoardNodeMouseEnter}
        handleBoardNodeMouseMove={handleBoardNodeMouseMove}
        clearBoardNodeTooltip={clearBoardNodeTooltip}
        handleBoardNodeTouchStart={handleBoardNodeTouchStart}
        resourceReadiness={resourceReadiness}
        isGestureKnifeActive={isGestureKnifeActive}
        sliceableWires={sliceableWires}
        sliceDependencyWires={sliceDependencyWires}
      />
      <BoardOverlays
        checklistHover={checklistHover}
        collectChecklistAvoidZones={collectChecklistAvoidZones}
        isMiniMapVisible={isMiniMapVisible}
        miniMapItems={miniMapItems}
        viewportState={viewportState}
        viewportSize={viewportSize}
        boardMetrics={boardMetrics}
        handleMiniMapPan={handleMiniMapPan}
        canEditStructure={canEditStructure}
        isCalendarShelfOpen={isCalendarShelfOpen}
        visionboardChecklistItems={visionboardChecklistItems}
        setIsCalendarShelfOpen={setIsCalendarShelfOpen}
        isClipboardWatcherEnabled={isClipboardWatcherEnabled}
        handleClipboardBlueprintExtract={handleClipboardBlueprintExtract}
        isMarkdownPanelOpen={isMarkdownPanelOpen}
        markdownMetadata={markdownMetadata}
        displayNodes={displayNodes}
        linkMarkdownRepository={linkMarkdownRepository}
        applyMarkdownInboundSync={applyMarkdownInboundSync}
      />
      <BoardDrawers
        draftDrawerNode={draftDrawerNode}
        setDraftDrawerNode={setDraftDrawerNode}
        displayNodes={displayNodes}
        workspaceMembers={workspaceMembers}
        baseOperationalPermissions={baseOperationalPermissions}
        persistDraftChecklist={persistDraftChecklist}
        drawerState={drawerState}
        selectedNode={selectedNode}
        recurringLens={recurringLens}
        closeChecklistNode={closeChecklistNode}
        lockedNodeIds={lockedNodeIds}
        canInteractWithChecklist={canInteractWithChecklist}
        updateTitle={updateTitle}
        updateWeightage={updateWeightage}
        updateNodeVerification={updateNodeVerification}
        updateNodeStatus={updateNodeStatus}
        updateNodeAssignment={updateNodeAssignment}
        selectedNodeOperationalAccess={selectedNodeOperationalAccess}
        canPerformOnNode={canPerformOnNode}
        canEditStructure={canEditStructure}
        canDeleteChecklist={canDeleteChecklist}
        deleteNode={deleteNode}
      />
      <BoardDialogs
        closeBoardDialogOpen={closeBoardDialogOpen}
        closeDialogError={closeDialogError}
        setCloseDialogError={setCloseDialogError}
        cancelBoardClose={cancelBoardClose}
        dialogBusyAction={dialogBusyAction}
        setDialogBusyAction={setDialogBusyAction}
        nodes={nodes}
        persistBoardSnapshot={persistBoardSnapshot}
        setCloseBoardDialogOpen={setCloseBoardDialogOpen}
        completeBoardClose={completeBoardClose}
        restoreBoard={restoreBoard}
        openGatesDialog={openGatesDialog}
        setOpenGatesDialog={setOpenGatesDialog}
        gateDialogError={gateDialogError}
        setGateDialogError={setGateDialogError}
        buildOpenGateCleanupSnapshot={buildOpenGateCleanupSnapshot}
        pendingDeletionsRef={pendingDeletionsRef}
      />
      </section>
    </div>
  )
}
